import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  VStack,
  Text,
  Heading,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Checkbox,
  SimpleGrid,
  Icon,
  Flex,
  HStack,
  Image,
  Divider,
  Container,
  Badge,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { FaBrain, FaHeartbeat, FaChartLine, FaLock, FaUserMd, FaShieldAlt, FaCheckCircle } from 'react-icons/fa';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';

const LoginHUD: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, signIn } = useAuth();
  const { setFirstName, setAcknowledged } = useUser();
  
  // Colors - using Methodist branding
  const bgColor = useColorModeValue('#f8f9fa', '#1a202c');
  const cardBg = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'white');
  const methodistBlue = '#003087';
  const methodistLightBlue = '#7BA4D9';
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Modal control
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // Form state
  const [firstName, setFirstNameState] = useState('');
  const [nameError, setNameError] = useState('');
  const [isAcknowledgedChecked, setIsAcknowledgedChecked] = useState(false);
  
  // Platform features for display
  const platformFeatures = [
    {
      title: 'Clinician Well-being',
      description: 'Comprehensive tools to monitor and improve physician wellness',
      icon: FaUserMd,
      color: methodistBlue,
    },
    {
      title: 'Stanford Model Integration',
      description: 'Based on the Stanford Model of Professional Fulfillment',
      icon: FaHeartbeat,
      color: methodistBlue,
    },
    {
      title: 'Data-Driven Insights',
      description: 'Analytics to identify burnout trends and intervention opportunities',
      icon: FaChartLine,
      color: methodistBlue,
    },
  ];
  
  // Check if we already have user info in localStorage and navigate directly
  useEffect(() => {
    const storedName = localStorage.getItem('jimi_firstName');
    const storedAcknowledged = localStorage.getItem('jimi_acknowledged') === 'true';
    
    if (storedName && storedAcknowledged && isAuthenticated) {
      setFirstNameState(storedName);
      setAcknowledged(true);
      // Get the redirect path from location state, or default to /presentation
      const from = (location.state as any)?.from?.pathname || '/presentation';
      navigate(from, { replace: true });
    }
  }, [setAcknowledged, isAuthenticated, navigate, location]);
  
  // Handle name submission
  const handleNameSubmit = async () => {
    if (firstName.trim()) {
      setNameError('');
      setFirstName(firstName);
      localStorage.setItem('jimi_firstName', firstName);
      onOpen();
    } else {
      setNameError('Please enter your name to continue');
    }
  };
  
  // Handle acknowledgment
  const handleAcknowledgment = async () => {
    try {
      localStorage.setItem('jimi_firstName', firstName);
      localStorage.setItem('jimi_acknowledged', 'true');
      setAcknowledged(true);
      
      // Get the redirect path from location state, or default to /presentation
      const from = (location.state as any)?.from?.pathname || '/presentation';
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Error during acknowledgment:', error);
      // Handle error appropriately
    }
  };
  
  return (
    <Box
      w="100vw"
      h="100vh"
      display="flex"
      justifyContent="center"
      alignItems="center"
      bg={bgColor}
      backgroundImage={`linear-gradient(135deg, ${methodistBlue}10, ${methodistLightBlue}10)`}
      position="relative"
      overflow="hidden"
    >
      {/* Background pattern */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        backgroundImage="url('/assets/pattern-bg.png')"
        backgroundSize="cover"
        opacity="0.05"
        zIndex="0"
      />
      
      {/* Main content */}
      <Container maxW="1200px" zIndex="1" py={8}>
        <Flex 
          direction={{ base: "column", lg: "row" }}
          align="center"
          justify="space-between"
          gap={10}
        >
          {/* Left side - Branding and Information */}
          <Box width={{ base: "100%", lg: "50%" }} mb={{ base: 8, lg: 0 }}>
            <Flex align="center" mb={6}>
              {/* Use text instead of images for logos since they're not available */}
              <Box 
                bg={methodistBlue} 
                color="white" 
                p={3} 
                borderRadius="md" 
                fontWeight="bold"
                fontSize="lg"
              >
                Houston Methodist
              </Box>
              <Divider orientation="vertical" height="40px" borderColor={methodistBlue} mx={4} />
              <Box 
                bg={methodistLightBlue} 
                color="white" 
                p={3} 
                borderRadius="md" 
                fontWeight="bold"
                fontSize="lg"
              >
                JIMI
              </Box>
            </Flex>
            
            <Heading 
              as="h1" 
              size="2xl" 
              mb={4} 
              color={methodistBlue}
              lineHeight="1.2"
            >
              JIMI Wellness Platform
            </Heading>
            
            <Text fontSize="xl" mb={6} color={textColor} fontWeight="medium" lineHeight="1.6">
              A comprehensive solution for clinician well-being and professional fulfillment
            </Text>
            
            <Badge 
              colorScheme="blue" 
              fontSize="md" 
              py={2} 
              px={4} 
              borderRadius="full"
              mb={8}
            >
              Seed FUNd Committee Presentation
            </Badge>
            
            <SimpleGrid columns={{ base: 1, md: 1 }} spacing={6} mt={8}>
              {platformFeatures.map((feature, index) => (
                <HStack 
                  key={index}
                  align="flex-start"
                  spacing={4}
                  p={4}
                  borderRadius="md"
                  transition="all 0.3s"
                >
                  <Flex
                    p={3}
                    borderRadius="md"
                    bg={feature.color}
                    color="white"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="md"
                  >
                    <Icon as={feature.icon} boxSize={5} />
                  </Flex>
                  <Box>
                    <Heading as="h3" size="md" mb={1}>
                      {feature.title}
                    </Heading>
                    <Text fontSize="md" color={textColor}>
                      {feature.description}
                    </Text>
                  </Box>
                </HStack>
              ))}
            </SimpleGrid>
          </Box>
          
          {/* Right side - Login Form */}
          <Box 
            width={{ base: "100%", lg: "50%" }}
            bg={cardBg}
            p={5}
            borderRadius="md"
            boxShadow="md"
            borderTop={`4px solid ${methodistBlue}`}
          >
            <VStack spacing={4} align="stretch">
              <Heading as="h2" size="md" color={methodistBlue} mb={1}>
                Welcome to the Presentation
              </Heading>
              
              <Text fontSize="sm" color={textColor} mb={2}>
                Please enter your name to begin the JIMI Wellness Platform demonstration.
              </Text>
              
              <FormControl isInvalid={!!nameError}>
                <FormLabel fontSize="sm" fontWeight="medium" color={textColor}>
                  Your Name
                </FormLabel>
                <Input
                  type="text"
                  placeholder="Enter your full name"
                  value={firstName}
                  onChange={(e) => setFirstNameState(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleNameSubmit()}
                  size="md"
                  height="40px"
                  fontSize="sm"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                  _hover={{ borderColor: methodistBlue }}
                  _focus={{ borderColor: methodistBlue, boxShadow: `0 0 0 1px ${methodistBlue}` }}
                  bg={useColorModeValue('white', 'gray.800')}
                  color={textColor}
                  mb={1}
                />
                {nameError && (
                  <FormErrorMessage fontSize="xs">{nameError}</FormErrorMessage>
                )}
              </FormControl>
              
              <HStack spacing={2} mt={1} mb={2}>
                <Icon as={FaLock} color={methodistBlue} boxSize={3} />
                <Text fontSize="xs" color="gray.500">
                  This presentation contains confidential information
                </Text>
              </HStack>
              
              <Button
                onClick={handleNameSubmit}
                rightIcon={<ChevronRightIcon />}
                size="md"
                bg={methodistBlue}
                _hover={{ bg: "#002060", transform: "translateY(-1px)", boxShadow: "md" }}
                _active={{ bg: "#001040" }}
                boxShadow="sm"
                px={4}
                height="40px"
                fontSize="md"
                color="white"
                fontWeight="bold"
                w="100%"
                mt={2}
              >
                Begin Presentation
              </Button>
            </VStack>
          </Box>
        </Flex>
      </Container>
      
      {/* Confidentiality Disclaimer Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(8px)" bg="rgba(0,0,0,0.7)" />
        <ModalContent borderRadius="lg" boxShadow="xl" mx={4} maxW="700px">
          <ModalHeader 
            bg={methodistBlue} 
            color="white" 
            borderTopRadius="lg" 
            py={4}
            px={6}
            fontSize="lg"
          >
            <Flex align="center">
              <Icon as={FaShieldAlt} mr={2} />
              Confidentiality Agreement
            </Flex>
          </ModalHeader>
          <ModalCloseButton color="white" />
          
          <ModalBody py={5} px={6}>
            <VStack spacing={4} align="stretch">
              <Text fontSize="md" fontWeight="medium" color={methodistBlue}>
                Confidentiality Notice
              </Text>
              
              <Text fontSize="sm" lineHeight="tall">
                This presentation contains confidential information regarding the JIMI Wellness Platform. By proceeding, you acknowledge and agree to the following terms:
              </Text>
              
              <Box 
                width="100%" 
                bg="#f8f9fa" 
                p={4} 
                borderRadius="md" 
                borderLeft="3px solid" 
                borderColor={methodistBlue}
              >
                <List spacing={3} fontSize="sm">
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color={methodistBlue} mt={1} />
                    <Text>
                      <Text as="span" fontWeight="bold">Confidential Information:</Text> All content is considered confidential and proprietary.
                    </Text>
                  </ListItem>
                  
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color={methodistBlue} mt={1} />
                    <Text>
                      <Text as="span" fontWeight="bold">Authorized Use:</Text> This presentation is intended for authorized stakeholders only.
                    </Text>
                  </ListItem>
                  
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color={methodistBlue} mt={1} />
                    <Text>
                      <Text as="span" fontWeight="bold">Non-Disclosure:</Text> You agree not to share or distribute any content without permission.
                    </Text>
                  </ListItem>
                  
                  <ListItem display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color={methodistBlue} mt={1} />
                    <Text>
                      <Text as="span" fontWeight="bold">Intellectual Property:</Text> All concepts, designs, and methodologies presented remain the intellectual property of the author/presenter.
                    </Text>
                  </ListItem>
                </List>
              </Box>
              
              <Checkbox 
                isChecked={isAcknowledgedChecked} 
                onChange={(e) => setIsAcknowledgedChecked(e.target.checked)}
                colorScheme="blue"
                size="md"
                spacing={2}
              >
                <Text fontSize="sm" fontWeight="medium">
                  I acknowledge and agree to maintain the confidentiality of all information presented.
                </Text>
              </Checkbox>
            </VStack>
          </ModalBody>
          
          <ModalFooter bg={useColorModeValue('gray.50', 'gray.700')} borderBottomRadius="lg" py={4} px={6}>
            <Button
              onClick={() => handleAcknowledgment()}
              isDisabled={!isAcknowledgedChecked}
              rightIcon={<ChevronRightIcon />}
              bg={methodistBlue}
              _hover={{ bg: "#002060" }}
              _active={{ bg: "#001040" }}
              color="white"
              fontWeight="bold"
              width="100%"
            >
              I Acknowledge and Proceed
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LoginHUD;
